import { useMemo } from 'react'
import { DashboardLayout } from '../../components/layout'
import { StatCard } from '../../components/ui'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useExpenses, useSubscriptions } from '../../hooks'
import { useAllTransactions } from '../../hooks/useTransactions'
import { formatCurrency } from '../../lib/format'

const MONTH_WINDOW = 12

function getMonthKey(dateValue) {
  if (!dateValue) return null
  const normalized = typeof dateValue === 'string' && dateValue.length >= 10
    ? `${dateValue.slice(0, 10)}T00:00:00`
    : dateValue
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return null
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function getMonthLabel(monthKey) {
  return new Date(`${monthKey}-01T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  })
}

function getMonthKeys(monthCount = MONTH_WINDOW) {
  const now = new Date()
  const keys = []
  for (let offset = monthCount - 1; offset >= 0; offset -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1)
    keys.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`)
  }
  return keys
}

function getMonthlySubscriptionAmount(subscription) {
  if (!subscription?.is_active) return 0
  const amount = parseFloat(subscription.amount) || 0
  if (subscription.frequency === 'yearly') return amount / 12
  if (subscription.frequency === 'quarterly') return amount / 3
  return amount
}

function FinancialsPage() {
  const { expenses, loading: expensesLoading } = useExpenses()
  const { subscriptions, loading: subscriptionsLoading } = useSubscriptions()
  const { transactions, loading: transactionsLoading } = useAllTransactions()

  const loading = expensesLoading || subscriptionsLoading || transactionsLoading

  const { currentMonth, yearTotals, monthlyRows } = useMemo(() => {
    const monthKeys = getMonthKeys()
    const emptySeries = monthKeys.reduce((acc, key) => {
      acc[key] = 0
      return acc
    }, {})

    const monthlySubscriptionBase = subscriptions.reduce((sum, sub) => (
      sum + getMonthlySubscriptionAmount(sub)
    ), 0)

    const subscriptionsByMonth = monthKeys.reduce((acc, key) => {
      acc[key] = monthlySubscriptionBase
      return acc
    }, {})

    const oneOffByMonth = { ...emptySeries }
    expenses.forEach(expense => {
      const monthKey = getMonthKey(expense.date)
      if (!monthKey || !(monthKey in oneOffByMonth)) return
      oneOffByMonth[monthKey] += parseFloat(expense.amount) || 0
    })

    const revenueByMonth = { ...emptySeries }
    transactions.forEach(transaction => {
      const type = transaction.type?.toLowerCase()
      if (type !== 'revenue' && type !== 'income') return
      const monthKey = getMonthKey(transaction.date)
      if (!monthKey || !(monthKey in revenueByMonth)) return
      revenueByMonth[monthKey] += parseFloat(transaction.amount) || 0
    })

    const rows = monthKeys.map(monthKey => {
      const subscriptionsTotal = subscriptionsByMonth[monthKey] || 0
      const oneOffTotal = oneOffByMonth[monthKey] || 0
      const combinedExpenses = subscriptionsTotal + oneOffTotal
      const revenue = revenueByMonth[monthKey] || 0
      const pnl = revenue - combinedExpenses

      return {
        monthKey,
        label: getMonthLabel(monthKey),
        subscriptions: subscriptionsTotal,
        oneOff: oneOffTotal,
        combined: combinedExpenses,
        revenue,
        pnl,
      }
    })

    const totals = rows.reduce((acc, row) => {
      acc.revenue += row.revenue
      acc.subscriptions += row.subscriptions
      acc.oneOff += row.oneOff
      acc.combined += row.combined
      acc.pnl += row.pnl
      return acc
    }, { revenue: 0, subscriptions: 0, oneOff: 0, combined: 0, pnl: 0 })

    return {
      currentMonth: rows[rows.length - 1] || {
        revenue: 0,
        subscriptions: 0,
        oneOff: 0,
        combined: 0,
        pnl: 0,
      },
      yearTotals: totals,
      monthlyRows: rows,
    }
  }, [expenses, subscriptions, transactions])

  if (loading) {
    return (
      <DashboardLayout title="Financials">
        <LoadingSpinner.Overlay message="Loading financials..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout title="Financials">
      <div className="grid grid-4 mb-8">
        <StatCard label="This Month Subscriptions" value={formatCurrency(currentMonth.subscriptions)} />
        <StatCard label="This Month One-Off" value={formatCurrency(currentMonth.oneOff)} />
        <StatCard label="This Month Combined" value={formatCurrency(currentMonth.combined)} changeType="negative" />
        <StatCard label="This Month P&L" value={formatCurrency(currentMonth.pnl)} changeType={currentMonth.pnl >= 0 ? 'positive' : 'negative'} />
      </div>

      <div className="card mb-8">
        <div className="card-header">
          <h3 className="card-title">Monthly Financial Breakdown (Last 12 Months)</h3>
        </div>
        <div className="card-body p-0">
          <table className="table">
            <thead>
              <tr>
                <th>Month</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Subscriptions</th>
                <th className="text-right">One-Off Expenses</th>
                <th className="text-right">Combined Expenses</th>
                <th className="text-right">P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {monthlyRows.map(row => (
                <tr key={row.monthKey}>
                  <td>{row.label}</td>
                  <td className="text-right">{formatCurrency(row.revenue)}</td>
                  <td className="text-right">{formatCurrency(row.subscriptions)}</td>
                  <td className="text-right">{formatCurrency(row.oneOff)}</td>
                  <td className="text-right">{formatCurrency(row.combined)}</td>
                  <td className={`text-right amount ${row.pnl >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrency(row.pnl)}
                  </td>
                </tr>
              ))}
              <tr className="font-bold" style={{ background: 'var(--gray-50)' }}>
                <td>Last 12 Months Total</td>
                <td className="text-right">{formatCurrency(yearTotals.revenue)}</td>
                <td className="text-right">{formatCurrency(yearTotals.subscriptions)}</td>
                <td className="text-right">{formatCurrency(yearTotals.oneOff)}</td>
                <td className="text-right">{formatCurrency(yearTotals.combined)}</td>
                <td className={`text-right amount ${yearTotals.pnl >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(yearTotals.pnl)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  )
}

export default FinancialsPage
